import { API } from '../../../services'
import { APIResponse, Filters, Pagination } from '../../../services/types'
import { defaultPagination, defaultSorting } from '../../../services/defaults'
import { defineStore } from 'pinia'
import { setPagination } from '../../../services/utils/utils'
import { useToast } from 'vuestic-ui'
import { isSuccessStatus } from '../../../utils/http'
import type { InventoryItemType } from '../types'

const { init } = useToast()

export const useItemTypeStore = defineStore('itemTypeStore', {
  state: () => ({
    loading: false,
    filters: {} as Filters,
    pagination: defaultPagination,
    sorting: defaultSorting,
    itemTypes: [] as InventoryItemType[],
    itemType: null as InventoryItemType | null,
  }),
  getters: {
    getItemTypes(state) {
      return state.itemTypes
    },
  },
  actions: {
    initLoading() {
      this.loading = true
    },
    stopLoading() {
      this.loading = false
    },
    setItemTypes(itemTypes: InventoryItemType[]) {
      this.itemTypes = itemTypes
    },
    setPagination(pagination: Pagination) {
      this.pagination = setPagination(pagination)
    },
    addItemType(itemType: any) {
      this.itemTypes.unshift(itemType)
    },
    editItemType(editedItemType: any) {
      const itemTypeIndex = this.itemTypes.findIndex((itemType) => itemType.uuid == editedItemType?.uuid)
      if (itemTypeIndex !== -1) {
        this.itemTypes[itemTypeIndex] = editedItemType
      }
    },
    removeItemType(itemTypeUuid: string) {
      const foundItemTypeIndex = this.itemTypes.findIndex((itemType) => itemType.uuid == itemTypeUuid)
      if (foundItemTypeIndex === -1) return
      this.itemTypes.splice(foundItemTypeIndex, 1)
    },
    async dispatchGetItemTypes(): Promise<any> {
      this.initLoading()

      const { status, data } = await API.itemType.getItemTypes(this.filters, this.pagination).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.setItemTypes(data.data)
        this.setPagination({
          meta: data.meta,
        })
      }

      return {
        success: isSuccessStatus(status),
      }
    },
    async dispatchCreateItemType(input: InventoryItemType): Promise<APIResponse<null>> {
      this.initLoading()

      const { status, data } = await API.itemType.createItemType(input).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.addItemType(data.data)
        init({ message: 'Item Type created successfully', color: 'success' })
      }

      return {
        success: isSuccessStatus(status),
      }
    },
    async dispatchDeleteItemType(itemType: InventoryItemType): Promise<APIResponse<null>> {
      this.initLoading()

      const { status } = await API.itemType.deleteItemType(itemType.uuid).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.removeItemType(itemType.uuid)
        init({ message: 'Item Type Deleted successfully', color: 'success' })
      }

      return {
        success: isSuccessStatus(status),
      }
    },
    async dispatchUpdateItemType(input: InventoryItemType): Promise<APIResponse<null>> {
      this.initLoading()

      const { status, data } = await API.itemType.updateItemType(input).finally(this.stopLoading)

      if (isSuccessStatus(status)) {
        this.editItemType(data.data)
        init({ message: 'Item Type Updated successfully', color: 'success' })
      }

      return {
        success: isSuccessStatus(status),
      }
    },
    async dispatchGetItemTypesByCategoryUuid(itemCategoryUuid: string): Promise<any> {
      this.initLoading()

      const { status, data } = await API.itemType
        .getItemTypes(undefined, undefined, undefined, itemCategoryUuid)
        .finally(this.stopLoading)

      return {
        success: isSuccessStatus(status),
        data: data?.data ?? [],
      }
    },
  },
})
